/**
 * Un écart entre deux montants, calculé à l'écran et mis en forme.
 *
 * ## Pourquoi ce fichier existe
 *
 * `formaterMontant` **refuse un nombre JavaScript à virgule** : `dec()` (moteur, `nombre.ts`)
 * lève une exception plutôt que de reprendre une valeur déjà approchée avant d'arriver chez lui.
 * C'est la garantie décimale du produit, et elle est juste.
 *
 * Mais une soustraction faite à l'écran — `Number(a) - Number(b)` — produit précisément un
 * nombre à virgule. Deux écrans le passaient tel quel à `formaterMontant`, et **rendaient la
 * page blanche** dès que l'écart n'était pas un nombre entier d'euros : rien n'attrape une
 * exception levée en plein rendu React, il n'y a pas de barrière d'erreur.
 *
 * Le défaut était intermittent, ce qui l'a rendu difficile à lire : une contre-étude soumise
 * juste après son import est identique au devis figé, l'écart tombe à zéro, le garde-fou du
 * seuil renvoie avant l'appel et l'écran s'affiche. La même retravaillée levait l'exception
 * (constaté sur APO250047 le 18/08/2026).
 *
 * Le passage par une chaîne à deux décimales est la seule forme que le moteur accepte — c'est
 * déjà l'usage de l'écran de déboursé. Il est ici plutôt que recopié une troisième fois.
 */
import { formaterMontant } from "@planif/moteur";

/**
 * En deçà, il n'y a pas d'écart : un demi-centime ne se montre pas.
 *
 * Ce n'est pas une tolérance de calcul — le moteur, lui, est exact — mais le seuil d'affichage
 * en dessous duquel l'arrondi à deux décimales rendrait « 0,00 € » précédé d'un signe.
 */
export const SEUIL_ECART = 0.005;

export type EcartAffichable = {
  /** Le signe, écrit séparément : c'est un signe moins typographique, pas un tiret. */
  readonly signe: "−" | "+";
  /** Le montant en valeur absolue, déjà mis en forme — « 1 234,56 ». */
  readonly montant: string;
  /** Un écart négatif est un gain : moins cher que la référence. */
  readonly classe: "conforme" | "en-ecart";
};

/**
 * Rend `null` quand il n'y a rien à montrer : écart nul, ou valeurs illisibles.
 *
 * Le contrôle de finitude n'est pas décoratif. `Number("")` vaut zéro et `Number("abc")` vaut
 * `NaN` ; sans lui, une valeur manquante repartirait vers `dec()`, qui lèverait — c'est-à-dire
 * la panne même que ce fichier existe pour fermer.
 */
export function ecartAffichable(delta: number): EcartAffichable | null {
  if (!Number.isFinite(delta)) return null;
  if (Math.abs(delta) < SEUIL_ECART) return null;
  return {
    signe: delta < 0 ? "−" : "+",
    // `.toFixed(2)` AVANT le moteur, jamais l'inverse : c'est tout l'objet de ce fichier.
    montant: formaterMontant(Math.abs(delta).toFixed(2)),
    classe: delta < 0 ? "conforme" : "en-ecart",
  };
}

/**
 * L'écart entre deux montants lus tels que le serveur les rend — des chaînes.
 *
 * Rend `null` si l'une des deux manque : un écart contre rien n'est pas un écart de zéro.
 */
export function ecartEntre(valeur: string | null, reference: string | null): EcartAffichable | null {
  const v = nombre(valeur);
  const r = nombre(reference);
  if (v === null || r === null) return null;
  return ecartAffichable(v - r);
}

/**
 * `Number("")` vaut **zéro**, pas `NaN` : sans ce contrôle, un montant manquant se lirait comme
 * un montant nul et l'écran annoncerait un écart de toute la valeur de la référence.
 */
function nombre(valeur: string | null): number | null {
  if (valeur === null || valeur.trim() === "") return null;
  const n = Number(valeur);
  return Number.isFinite(n) ? n : null;
}
